/**
 * RbacMatrix — Roles × Capabilities grid with super_admin edit mode.
 *
 * Backend source of truth: GET /admin/rbac/matrix  +  PUT /admin/rbac/matrix.
 * Layout: left column = capability (grouped, searchable); header row = roles;
 * cells = ✓/✗ (color-coded). Admin capabilities are badged with "admin".
 *
 * Edit mode (super_admin only):
 *  • Toggle a cell with one click; staged-but-not-saved cells get a yellow
 *    dot + outline so the admin can see what's dirty.
 *  • Per-role column header shows a bulk toggle ("All / None") that flips
 *    every cap in that role in one click.
 *  • Save (PUT) and Revert (drop staged edits) sit in the toolbar; both
 *    are disabled until something is dirty.
 *  • Non-super_admin readers see the same matrix but no edit affordances.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Check, X, Search, AlertTriangle, Shield, Loader2, Pencil, Save, RotateCcw, MoreHorizontal, Clock, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES_API = `${API}/admin/rbac/matrix`;
const AUDIT_API = `${API}/admin/rbac/audit`;


// ─── relative time helper ─────────────────────────────────────────────
// Keeps the banner text human-readable without pulling in date-fns just
// for one component (saves ~30KB on the Settings bundle).
function _relTime(iso) {
  if (!iso) return "";
  const dt = typeof iso === "string" ? new Date(iso) : iso;
  const seconds = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (seconds < 60)      return `${seconds}s ago`;
  if (seconds < 3600)    return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400)   return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400*7) return `${Math.floor(seconds / 86400)}d ago`;
  return dt.toLocaleDateString();
}


/** Recent audit-trail banner above the matrix.
 *  Latest entry inline, collapsed list below for review. Hidden if no audit. */
function AuditBanner({ audit, expanded, setExpanded }) {
  if (!audit || audit.length === 0) return null;
  const latest = audit[0];
  const others = audit.slice(1);
  const actor = latest.actor_email || latest.actor_id || "Someone";
  const action = latest.action === "rbac.matrix.reset" ? "reset the matrix to baseline" : `changed ${latest.change_count} permission${latest.change_count === 1 ? "" : "s"}`;
  return (
    <div data-testid="rbac-matrix-audit-banner" style={{ marginBottom: 12, padding: "10px 14px", background: "#F8FAFC", border: "0.5px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#334155" }}>
          <Clock style={{ width: 12, height: 12, color: "#64748B" }} />
          <span data-testid="rbac-audit-latest">
            <strong>{actor}</strong> {action} <span style={{ color: "#64748B" }}>· {_relTime(latest.at)}</span>
          </span>
        </div>
        {others.length > 0 && (
          <button
            data-testid="rbac-audit-toggle"
            onClick={() => setExpanded(e => !e)}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#475569", background: "none", border: "none", cursor: "pointer" }}
          >
            {expanded ? <><ChevronUp style={{ width: 11, height: 11 }} /> Hide history</> : <><ChevronDown style={{ width: 11, height: 11 }} /> {others.length} earlier change{others.length === 1 ? "" : "s"}</>}
          </button>
        )}
      </div>
      {expanded && others.length > 0 && (
        <div data-testid="rbac-audit-list" style={{ marginTop: 8, paddingTop: 8, borderTop: "0.5px dashed #CBD5E1" }}>
          {others.map(e => (
            <div key={e.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 11, color: "#475569" }}>
              <span>
                <strong>{e.actor_email || e.actor_id || "?"}</strong>{" "}
                {e.action === "rbac.matrix.reset" ? "reset matrix" : `changed ${e.change_count} perm${e.change_count === 1 ? "" : "s"}`}
              </span>
              <span style={{ color: "#94A3B8" }}>{_relTime(e.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RbacMatrix() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const [editing, setEditing] = useState(false);
  // Staged matrix overlay: { [role]: { [cap]: bool } }  — sparse, only changed cells
  const [staged, setStaged] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  // Recent audit trail — shown collapsed above the matrix so reviewers can
  // spot accidental edits at a glance without opening the full audit log.
  const [audit, setAudit] = useState([]);
  const [auditExpanded, setAuditExpanded] = useState(false);

  const loadAudit = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const r = await axios.get(`${AUDIT_API}?limit=10`, { headers: { Authorization: `Bearer ${token}` } });
      setAudit(r.data?.entries || []);
    } catch (_e) { /* non-critical */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(ROLES_API, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load RBAC matrix");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadAudit(); }, [load, loadAudit]);

  // Effective view = persisted matrix overlaid with the user's staged edits.
  const effective = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const role of data.roles) {
      out[role.key] = { ...(data.matrix[role.key] || {}), ...(staged[role.key] || {}) };
    }
    return out;
  }, [data, staged]);

  const isDirty = useMemo(() => Object.keys(staged).some(r => Object.keys(staged[r] || {}).length > 0), [staged]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    const caps = data.capabilities;
    if (!needle) return caps;
    return caps.filter(c =>
      c.key.toLowerCase().includes(needle) ||
      (c.label || "").toLowerCase().includes(needle) ||
      (c.description || "").toLowerCase().includes(needle) ||
      (c.group || "").toLowerCase().includes(needle)
    );
  }, [data, q]);

  const toggleCell = (role, cap) => {
    if (!editing) return;
    setStaged(prev => {
      const baseline = !!(data.matrix[role] || {})[cap];
      const currentEffective = staged[role]?.[cap] !== undefined ? staged[role][cap] : baseline;
      const next = !currentEffective;
      const nextRole = { ...(prev[role] || {}) };
      if (next === baseline) {
        // Toggled back to original — drop the stage entry so the dirty count
        // accurately reflects "edits that will be persisted".
        delete nextRole[cap];
      } else {
        nextRole[cap] = next;
      }
      const out = { ...prev };
      if (Object.keys(nextRole).length === 0) delete out[role];
      else out[role] = nextRole;
      return out;
    });
  };

  const bulkRoleSet = (role, value) => {
    if (!editing || !data) return;
    setStaged(prev => {
      const baselineRow = data.matrix[role] || {};
      const nextRole = {};
      for (const c of data.capabilities) {
        const baseline = !!baselineRow[c.key];
        if (value !== baseline) nextRole[c.key] = value;
      }
      const out = { ...prev };
      if (Object.keys(nextRole).length === 0) delete out[role];
      else out[role] = nextRole;
      return out;
    });
  };

  const revert = () => setStaged({});

  const save = async () => {
    if (!isDirty) return;
    setSaving(true);
    setSaveErr(null);
    try {
      const token = localStorage.getItem("token");
      const fullMatrix = {};
      for (const role of data.roles) fullMatrix[role.key] = effective[role.key];
      const res = await axios.put(ROLES_API, { matrix: fullMatrix }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setData(res.data);
      setStaged({});
      setEditing(false);
      loadAudit();  // pull the new audit row our PUT just emitted
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setSaveErr(typeof detail === "string" ? detail : (detail?.code || "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div data-testid="rbac-matrix-loading" style={{ padding: 40, textAlign: "center", color: "#888" }}><Loader2 className="animate-spin" style={{ display: "inline", width: 14, height: 14, marginRight: 6 }} /> Loading matrix…</div>;
  if (err) return <div data-testid="rbac-matrix-error" style={{ padding: 20, background: "#FFEBEB", color: "#A32D2D", borderRadius: 8, border: "0.5px solid #F1B3B3", fontSize: 12 }}><AlertTriangle style={{ display: "inline", width: 12, height: 12, marginRight: 4 }} /> {err}</div>;
  if (!data) return null;

  const canEdit = !!data.can_edit;
  const dirtyCount = Object.values(staged).reduce((n, row) => n + Object.keys(row || {}).length, 0);

  // Group capabilities for visual section headers.
  const groups = {};
  for (const c of filtered) {
    const g = c.group || "Other";
    (groups[g] = groups[g] || []).push(c);
  }
  const groupOrder = Object.keys(groups);

  return (
    <div data-testid="rbac-matrix">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 650 }}>Roles & Permissions</div>
          <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>
            {canEdit
              ? (editing ? "Editing — click any cell to toggle. Changes are staged until you Save." : "Click Edit to change role capabilities. Persisted overrides apply to every user with that role.")
              : "Read-only view of every role's capabilities. Use this before inviting or changing a user role."}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ position: "relative", width: 220 }}>
            <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "#888" }} />
            <input
              data-testid="rbac-matrix-search"
              className="input"
              placeholder="Filter capabilities…"
              value={q}
              onChange={e => setQ(e.target.value)}
              style={{ paddingLeft: 26, fontSize: 12 }}
            />
          </div>
          {canEdit && !editing && (
            <button
              data-testid="rbac-matrix-edit-btn"
              onClick={() => setEditing(true)}
              className="btn-secondary"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "6px 12px" }}
            >
              <Pencil style={{ width: 12, height: 12 }} /> Edit
            </button>
          )}
          {canEdit && editing && (
            <>
              <span data-testid="rbac-matrix-dirty-count" style={{ fontSize: 11, color: dirtyCount > 0 ? "#854F0B" : "#888" }}>
                {dirtyCount > 0 ? `${dirtyCount} change${dirtyCount === 1 ? "" : "s"} staged` : "No changes"}
              </span>
              <button
                data-testid="rbac-matrix-revert-btn"
                onClick={revert}
                disabled={!isDirty || saving}
                className="btn-secondary"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "6px 12px", opacity: (!isDirty || saving) ? 0.5 : 1 }}
              >
                <RotateCcw style={{ width: 12, height: 12 }} /> Revert
              </button>
              <button
                data-testid="rbac-matrix-save-btn"
                onClick={save}
                disabled={!isDirty || saving}
                className="btn-primary"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "6px 12px", opacity: (!isDirty || saving) ? 0.5 : 1 }}
              >
                {saving ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <Save style={{ width: 12, height: 12 }} />} Save
              </button>
              <button
                data-testid="rbac-matrix-cancel-btn"
                onClick={() => { setStaged({}); setEditing(false); setSaveErr(null); }}
                className="btn-secondary"
                style={{ fontSize: 12, padding: "6px 12px" }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {saveErr && (
        <div data-testid="rbac-matrix-save-error" style={{ marginBottom: 10, padding: 10, background: "#FFEBEB", color: "#A32D2D", borderRadius: 8, border: "0.5px solid #F1B3B3", fontSize: 12 }}>
          <AlertTriangle style={{ display: "inline", width: 12, height: 12, marginRight: 4 }} /> {saveErr}
        </div>
      )}

      <AuditBanner audit={audit} expanded={auditExpanded} setExpanded={setAuditExpanded} />

      <div data-testid="rbac-matrix-scroll" style={{ overflow: "auto", maxHeight: "70vh", border: "0.5px solid var(--border)", borderRadius: 8, background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 11, minWidth: 900 }}>
          <thead>
            <tr>
              <th data-testid="rbac-matrix-corner" style={{ textAlign: "left", padding: "12px 12px", fontSize: 10, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid #d1d5db", borderRight: "1px solid #e5e7eb", minWidth: 240, width: 240, position: "sticky", left: 0, top: 0, background: "#f9fafb", zIndex: 4, boxShadow: "0 1px 0 rgba(0,0,0,0.04)" }}>
                Capability
              </th>
              {data.roles.map(r => (
                <th key={r.key} data-testid={`rbac-matrix-role-header-${r.key}`} style={{ padding: "10px 8px", fontSize: 10, fontWeight: 700, color: "#374151", textAlign: "center", borderBottom: "1px solid #d1d5db", whiteSpace: "nowrap", position: "sticky", top: 0, background: "#f9fafb", zIndex: 3, minWidth: 96, boxShadow: "0 1px 0 rgba(0,0,0,0.04)" }}>
                  <div>{r.label}</div>
                  {editing && (
                    <div style={{ marginTop: 4, display: "inline-flex", gap: 4 }}>
                      <button
                        data-testid={`rbac-matrix-bulk-allow-${r.key}`}
                        title={`Grant every capability to ${r.label}`}
                        onClick={() => bulkRoleSet(r.key, true)}
                        style={{ fontSize: 9, padding: "2px 6px", border: "0.5px solid #c8e6c9", borderRadius: 4, background: "#f1f8e9", color: "#33691e", cursor: "pointer" }}
                      >
                        all
                      </button>
                      <button
                        data-testid={`rbac-matrix-bulk-deny-${r.key}`}
                        title={`Revoke every capability from ${r.label}`}
                        onClick={() => bulkRoleSet(r.key, false)}
                        style={{ fontSize: 9, padding: "2px 6px", border: "0.5px solid #ffcdd2", borderRadius: 4, background: "#ffebee", color: "#b71c1c", cursor: "pointer" }}
                      >
                        none
                      </button>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupOrder.map(g => (
              <React.Fragment key={g}>
                <tr>
                  <td colSpan={data.roles.length + 1} style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, background: "#eff6ff", color: "#1d4ed8", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid #bfdbfe", borderTop: "1px solid #bfdbfe", position: "sticky", left: 0 }}>
                    {g}
                  </td>
                </tr>
                {groups[g].map((cap, idx) => (
                  <tr key={cap.key} data-testid={`rbac-matrix-row-${cap.key}`} style={{ borderBottom: "1px solid #f3f4f6", background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td title={cap.description} style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-secondary)", position: "sticky", left: 0, background: idx % 2 === 0 ? "#fff" : "#fafafa", borderRight: "1px solid #e5e7eb", verticalAlign: "top", minWidth: 240, width: 240, zIndex: 1 }}>
                      <div style={{ fontWeight: 600, color: "#111827", lineHeight: 1.35 }}>{cap.label || cap.key}</div>
                      <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                        <code style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{cap.key}</code>
                        {(cap.flags || []).includes("admin") && <span style={{ marginLeft: 6, background: "#FFF8F0", color: "#854F0B", padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700 }}>ADMIN</span>}
                      </div>
                    </td>
                    {data.roles.map(r => {
                      const has = !!effective[r.key]?.[cap.key];
                      const isStaged = staged[r.key]?.[cap.key] !== undefined;
                      const baselineHas = !!(data.matrix[r.key] || {})[cap.key];
                      const isOverrideFromServer = (data.overrides?.[r.key]?.[cap.key] !== undefined);
                      const showDirty = isStaged && has !== baselineHas;
                      return (
                        <td
                          key={r.key}
                          data-testid={`rbac-matrix-cell-${r.key}-${cap.key}`}
                          data-granted={has}
                          data-dirty={showDirty || undefined}
                          onClick={() => toggleCell(r.key, cap.key)}
                          style={{
                            padding: "8px 8px",
                            textAlign: "center",
                            verticalAlign: "middle",
                            cursor: editing ? "pointer" : "default",
                            background: showDirty ? "#FFF8E1" : (isOverrideFromServer && !editing ? "#F3E8FF" : undefined),
                            outline: showDirty ? "1.5px solid #F59E0B" : undefined,
                            outlineOffset: -2,
                            transition: "background 120ms",
                          }}
                        >
                          {has
                            ? <Check style={{ width: 14, height: 14, color: "#0F6E56" }} />
                            : <X style={{ width: 14, height: 14, color: "#cbd5e1" }} />}
                          {showDirty && <span title="Staged — not saved yet" style={{ display: "inline-block", marginLeft: 4, width: 5, height: 5, borderRadius: "50%", background: "#F59E0B" }} />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={data.roles.length + 1} data-testid="rbac-matrix-empty" style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#888" }}>
                  No capabilities match "{q}".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: "var(--text-tertiary)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Shield style={{ width: 11, height: 11 }} />
          Admin-only capabilities are badged. Delegation powers are shown in the Role Preview when inviting or editing a user.
        </span>
        {!editing && Object.keys(data.overrides || {}).length > 0 && (
          <span data-testid="rbac-matrix-overrides-note" style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#F3E8FF", color: "#6B21A8", padding: "2px 8px", borderRadius: 12 }}>
            <MoreHorizontal style={{ width: 11, height: 11 }} />
            {Object.keys(data.overrides).length} role(s) have persisted overrides (highlighted in purple)
          </span>
        )}
      </div>
    </div>
  );
}
